import React, { useState, useEffect, useRef } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Send, Loader2, Zap, CheckCircle, AlertTriangle, Play } from 'lucide-react';

const Chat = () => {
  const { currentProjectId, projectInfo } = useProject();
  const { api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingTasks, setPendingTasks] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!currentProjectId) return;
      try {
        const data = await api(`/api/chat/history?project_id=${currentProjectId}&limit=30`);
        setMessages(data);
      } catch (err) {
        console.error('Failed to fetch chat history:', err);
      }
    };
    fetchHistory();
  }, [api, currentProjectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);
    setPendingTasks(null);

    // Add user message to UI
    setMessages(prev => [...prev, {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    }]);

    try {
      const response = await api('/api/chat/message', {
        method: 'POST',
        body: JSON.stringify({
          project_id: currentProjectId,
          message: userMessage
        })
      });

      const parsed = response.parsed || {};
      
      // Add assistant response
      setMessages(prev => [...prev, {
        message_id: response.message_id,
        role: 'assistant',
        content: parsed.explanation || 'Processing your request...',
        agent_responses: [parsed],
        created_at: new Date().toISOString()
      }]);

      // If there are tasks to execute, show them
      if (parsed.tasks && parsed.tasks.length > 0) {
        setPendingTasks({
          tasks: parsed.tasks,
          requires_approval: parsed.requires_approval,
          estimated_cost: parsed.estimated_cost
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
      setMessages(prev => [...prev, {
        message_id: `error_${Date.now()}`,
        role: 'assistant',
        content: `Error: ${err.message}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setSending(false);
    }
  };

  const executeTasks = async () => {
    if (!pendingTasks?.tasks) return;

    setSending(true);
    try {
      const response = await api('/api/chat/execute', {
        method: 'POST',
        body: JSON.stringify({
          project_id: currentProjectId,
          tasks: pendingTasks.tasks
        })
      });

      toast.success('Tasks executed successfully');
      
      // Add results to chat
      setMessages(prev => [...prev, {
        message_id: response.message_id,
        role: 'assistant',
        content: `Executed ${pendingTasks.tasks.length} tasks`,
        agent_responses: response.results,
        created_at: new Date().toISOString()
      }]);

      setPendingTasks(null);
    } catch (err) {
      toast.error(err.message || 'Failed to execute tasks');
    } finally {
      setSending(false);
    }
  };

  const quickCommands = [
    { label: 'Scan website', command: `Scan ${projectInfo.domain} for issues` },
    { label: 'Find 10 leads', command: `Find 10 qualified leads for ${projectInfo.name}` },
    { label: 'Create image', command: `Create a marketing image for ${projectInfo.name}` },
    { label: 'Check costs', command: "Show me today's costs and usage" }
  ];

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <MessageSquare className="w-6 h-6" style={{ color: projectInfo.color }} />
          Agent Chat
        </h1>
        <p className="text-muted-foreground">
          Give instructions to your AI agents in plain English
        </p>
      </div>

      {/* Quick Commands */}
      <div className="flex flex-wrap gap-2 mb-4">
        {quickCommands.map((cmd, idx) => (
          <Button
            key={idx}
            variant="outline"
            size="sm"
            onClick={() => setInput(cmd.command)}
            data-testid={`quick-cmd-${idx}`}
          >
            <Zap className="w-3 h-3 mr-1" />
            {cmd.label}
          </Button>
        ))}
      </div>

      {/* Chat Messages */}
      <Card className="flex-1 bg-card border-border overflow-hidden flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">Start a conversation</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Type an instruction like "Find 50 electrician leads in Perth" or use the quick commands above.
                </p>
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`chat-bubble ${msg.role}`}
                data-testid={`chat-message-${msg.role}`}
              >
                <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                {msg.agent_responses && msg.agent_responses.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {msg.agent_responses.map((resp, idx) => (
                      <div key={idx} className="text-xs p-2 bg-black/20 rounded">
                        {resp.tasks && (
                          <div className="mb-1">
                            <Badge variant="outline" className="text-xs">
                              {resp.tasks.length} tasks
                            </Badge>
                            {resp.estimated_cost > 0 && (
                              <Badge variant="outline" className="text-xs ml-1">
                                ~${resp.estimated_cost.toFixed(3)}
                              </Badge>
                            )}
                          </div>
                        )}
                        {resp.error && (
                          <div className="text-red-400">{resp.error}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(msg.created_at).toLocaleTimeString()}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Pending Tasks Preview */}
        {pendingTasks && (
          <div className="p-4 border-t border-border bg-amber-500/5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="font-medium text-sm">Ready to execute {pendingTasks.tasks.length} tasks</span>
              </div>
              {pendingTasks.estimated_cost > 0 && (
                <Badge variant="outline">Est. ~${pendingTasks.estimated_cost.toFixed(3)}</Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              {pendingTasks.tasks.map((t, i) => (
                <div key={i}>• {t.agent_type}: {t.action}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={executeTasks} disabled={sending} data-testid="execute-tasks-btn">
                <Play className="w-3 h-3 mr-1" />
                Execute
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPendingTasks(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-border">
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type an instruction..."
              disabled={sending}
              className="flex-1"
              data-testid="chat-input"
            />
            <Button type="submit" disabled={sending || !input.trim()} data-testid="send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default Chat;
